import React from 'react';
import { first, isEmpty } from 'lodash';
import uuid from 'uuid-random';
import { IHighlighted } from '../contexts/styledWordContext';
import { ExemplarContext as EmergentContext } from '../contexts/emergentContext';
import { ExemplarContext as LaterContext } from '../contexts/laterContext';
import { parseWordsAndSymbols } from './textEditor/common';
import { Tool, TOOLS } from '../constants/edit';
import {
  LANGUAGE_FEATURES,
  TEXT_PRESENTATION,
  TEXT_STRUCTURE,
  WORDS,
  WORDS_AND_SENTENCES,
} from '../constants/navigation';
import {
  WORDS as WORDS_LABEL,
  SENTENCES as SENTENCES_LABEL,
  WORDS_AND_SENTENCES as WORDS_AND_SENTENCES_LABEL,
} from '../constants/exemplar';
import { CONSOLIDATING, DEVELOPING, EARLY, EMERGENT, EXTENDING } from '../constants';
import {
  Exemplar,
  EditedExemplarContent,
  INote,
  LineType,
  SavedNote,
  SavedPenLine,
  Section,
  EditableSection,
  Image,
  LaterExemplar,
  EmergentExemplar,
  WordsAndSentencesContent,
  EditedEmergentExemplarContent,
  ILabel,
  SavedLabel,
  WordsPageContent,
  ContentLine,
  IImage,
  EditedWordsPageTitle,
  WordsPageTitle,
} from '../types';
import {
  AddLabel,
  HideLabel,
  AddNote,
  HideNotes,
  ShowNotes,
  Highlight,
  HideHighlights,
  Draw,
  HideDrawings,
  ImageEditor,
  Undo,
  Redo,
  Reset,
  HideText,
  ShowHiddenText,
  Close,
  Save,
  EditText,
} from '../assets/icons/EditTool';
import { LIMIT_DRAG_ELEMENTS } from '../components/DraggableLabel/ContextDraggableLabel';
import { Flex, FlexBehavior, FlexJustify } from 'react-magma-dom';
import { getHiddenImageId } from '.';

export const DEFAULT_STATE: Record<string, boolean> = {
  addLabel: false,
  hideLabel: false,
  highlight: false,
  hideHighlights: false,
  addNote: false,
  hideNotes: false,
  pen: false,
  hideDrawings: false,
  hideText: false,
  imageEditor: false,
};

export const EDIT_TOOL: Record<string, Tool[]> = {
  words: [
    TOOLS.addLabel,
    TOOLS.hideLabel,
    TOOLS.pen,
    TOOLS.hideDrawings,
    TOOLS.imageEditor,
    TOOLS.undo,
    TOOLS.redo,
    TOOLS.reset,
  ],
  sentences: [
    TOOLS.editText,
    TOOLS.pen,
    TOOLS.hideDrawings,
    TOOLS.highlight,
    TOOLS.hideHighlights,
    TOOLS.hideText,
    TOOLS.imageEditor,
    TOOLS.undo,
    TOOLS.redo,
    TOOLS.reset,
  ],
  [WORDS_AND_SENTENCES]: [
    TOOLS.editText,
    TOOLS.pen,
    TOOLS.hideDrawings,
    TOOLS.highlight,
    TOOLS.hideHighlights,
    TOOLS.hideText,
    TOOLS.imageEditor,
    TOOLS.undo,
    TOOLS.redo,
    TOOLS.reset,
  ],
  [TEXT_PRESENTATION]: [
    TOOLS.editText,
    TOOLS.addNote,
    TOOLS.hideNotes,
    TOOLS.pen,
    TOOLS.hideDrawings,
    TOOLS.highlight,
    TOOLS.hideHighlights,
    TOOLS.hideText,
    TOOLS.imageEditor,
    TOOLS.undo,
    TOOLS.redo,
    TOOLS.reset,
  ],
  [TEXT_STRUCTURE]: [
    TOOLS.editText,
    TOOLS.addNote,
    TOOLS.hideNotes,
    TOOLS.pen,
    TOOLS.hideDrawings,
    TOOLS.highlight,
    TOOLS.hideHighlights,
    TOOLS.hideText,
    TOOLS.undo,
    TOOLS.redo,
    TOOLS.reset,
  ],
  [LANGUAGE_FEATURES]: [
    TOOLS.editText,
    TOOLS.addNote,
    TOOLS.hideNotes,
    TOOLS.pen,
    TOOLS.hideDrawings,
    TOOLS.hideText,
    TOOLS.undo,
    TOOLS.redo,
    TOOLS.reset,
  ],
};

// TODO: TEMPORARY
export const notAvailableTools = [TOOLS.undo, TOOLS.redo];

export const AddLabelLimitReached = (
  <span>
    Label limit reached.
    <br />
    <Flex behavior={FlexBehavior.container} justify={FlexJustify.center}>
      Max. {LIMIT_DRAG_ELEMENTS}.
    </Flex>
  </span>
);

export const getEditLabel = (isNotesExist: boolean, tool: string): string => {
  const tools: Record<string, string> = {
    editText: 'Text editor',
    addLabel: 'Add label',
    hideLabel: 'Hide labels',
    addNote: 'Add note',
    hideNotes: isNotesExist ? 'Hide notes' : 'Show notes',
    highlight: 'Highlight word',
    pen: 'Pen',
    hideDrawings: 'Hide pen',
    reset: 'Reset',
    hideText: 'Hide text',
    hideHighlights: 'Hide highlights',
    imageEditor: 'Image Editor',
    undo: 'Undo',
    redo: 'Redo',
    save: 'Save',
    close: 'Close',
  };

  return tools[tool];
};

export const getEditIcon = (
  isNotesHidden: boolean,
  isTextHidden: boolean,
  tool: string,
  color: string
): JSX.Element => {
  const tools: Record<string, JSX.Element> = {
    addLabel: <AddLabel />,
    hideLabel: <HideLabel />,
    addNote: <AddNote color={color} />,
    hideNotes: isNotesHidden ? <ShowNotes /> : <HideNotes />,
    highlight: <Highlight />,
    hideHighlights: <HideHighlights />,
    pen: <Draw />,
    hideDrawings: <HideDrawings />,
    editText: <EditText />,
    imageEditor: <ImageEditor />,
    undo: <Undo />,
    redo: <Redo />,
    reset: <Reset />,
    hideText: isTextHidden ? <ShowHiddenText /> : <HideText />,
    save: <Save />,
    close: <Close />,
  };

  return tools[tool];
};

export const getEditedExemplarContent = (
  stage: string,
  exemplarContent: Exemplar['content'],
  exemplarTitle: string,
  hiddenImages: Array<string | number> = [],
  wordsImages: Array<IImage> = [],
  labels: ILabel[] = []
): EditedEmergentExemplarContent | EditedExemplarContent => {
  switch (stage) {
    case EMERGENT:
      return getEmergentContent(
        exemplarContent as EmergentExemplar['content'],
        exemplarTitle,
        hiddenImages,
        wordsImages,
        labels
      );
    case EARLY:
    default:
      return getEditedContent(exemplarContent as LaterExemplar['content'], hiddenImages);
  }
};

export const getEmergentContent = (
  exemplarContent: EmergentExemplar['content'],
  exemplarTitle: string,
  hiddenImages: Array<string | number> = [],
  wordsImages: Array<IImage> = [],
  labels: ILabel[]
): EditedEmergentExemplarContent => {
  const emergentContent: EditedEmergentExemplarContent = [];

  exemplarContent.forEach(screen => {
    if (screen.label === WORDS_LABEL) {
      const title = (screen as WordsPageContent).title
        ? computeWordsScreenTitle((screen as WordsPageContent).title as WordsPageTitle)
        : convertStringToTextObject(exemplarTitle);
      const savedLabels = computeSavedLabels(labels, (screen as WordsPageContent).words);
      const savedImages = computeSavedWordsImages(wordsImages, (screen as WordsPageContent).images);

      emergentContent[0] = {
        ...screen,
        title,
        words: savedLabels,
        images: savedImages,
      };
    }

    if (screen.label === SENTENCES_LABEL) {
      emergentContent[1] = {
        ...screen,
        content: getEditedContent((screen as WordsAndSentencesContent).content, hiddenImages),
      };
    }

    if (screen.label === WORDS_AND_SENTENCES_LABEL) {
      emergentContent[2] = {
        ...screen,
        content: getEditedContent((screen as WordsAndSentencesContent).content, hiddenImages),
      };
    }
  });

  return emergentContent;
};

export const getEditedContent = (
  exemplarContent: LaterExemplar['content'] | WordsAndSentencesContent['content'],
  hiddenImages: Array<string | number> = []
): EditedExemplarContent => {
  return Object.entries(exemplarContent).reduce(
    (content, [sectionName, sectionContent]) => {
      return {
        ...content,
        [sectionName]: sectionContent.map((sectionItem: EditableSection) => {
          const { id: sectionId, defaultImageDimensions, isPlaceholder, ...sectionItemProps } = sectionItem;
          let isHidden = false;
          if (sectionItem.type === 'image') {
            const { default_uuid, uuid } = sectionItem;
            const hiddenImageId = getHiddenImageId(default_uuid, uuid);
            isHidden = hiddenImages.includes(hiddenImageId);
          }

          return {
            ...sectionItemProps,
            type: sectionItem.type,
            ...(isPlaceholder && !uuid.test((sectionItem as Image).uuid) && { isPlaceholder: true }),
            ...(sectionItem.type === 'image' && { isHidden }),
            ...((sectionItem as Section).lines && {
              lines: (sectionItem as Section).lines.map(line => {
                const { id: lineId, ...lineProps } = line;

                return {
                  ...lineProps,
                  words: line.words.map(word => {
                    return {
                      text: word.text,
                      ...(word.style?.length && { style: word.style }),
                      ...(word.language && { language: word.language }),
                      ...(word.prepositional_phrase && { prepositional_phrase: word.prepositional_phrase }),
                      ...(word.phrase && { phrase: word.phrase }),
                      ...(word.text_type && { text_type: word.text_type }),
                      ...(word.color && { color: word.color }),
                      ...(word.highlight && { highlight: word.highlight }),
                    };
                  }),
                };
              }),
            }),
          };
        }),
      };
    },
    { left: [], right: [] }
  );
};

export const convertStringToTextObject = (text: string): EditedWordsPageTitle => {
  const lines = text?.split(/\r|\r\n|\n/) || [];

  return {
    lines: lines.map(line => {
      return {
        words: line
          ? parseWordsAndSymbols(line).map(word => ({
              text: word,
            }))
          : [],
      };
    }),
  };
};

export const hasHighlightsInContent = (
  exemplar: LaterExemplar | EmergentExemplar,
  stage: string,
  page: string
): boolean => {
  if (isEmpty(exemplar)) {
    return false;
  }

  if (stage === EMERGENT) {
    const emergent = exemplar as EmergentExemplar;

    /** Highlights not allowed on "Words" page */
    if (page === WORDS) {
      return false;
    }

    const emergentContent = emergent.content as WordsAndSentencesContent[];
    const wordsAndSentencesContent = getEmergentContentByPage(emergentContent, page);
    const laterOrEmergentExemplar = wordsAndSentencesContent as WordsAndSentencesContent;
    const emergentHighlights = getHighlightsFromSavedContent(laterOrEmergentExemplar.content);

    return Object.keys(emergentHighlights).length > 0;
  }

  const laterHighlights = getHighlightsFromSavedContent(exemplar.content as LaterExemplar['content']);

  return Object.keys(laterHighlights).length > 0;
};

const getEmergentContentByPage = (content: WordsAndSentencesContent[], page: string) => {
  return first(
    content.filter(item => {
      const searchRegExp = /\s/g;
      const replaceWith = '-';
      const emergentLabel = item.label.toLowerCase().replace(searchRegExp, replaceWith);

      return emergentLabel === page;
    })
  );
};

export const getHighlightsFromSavedContent = (
  content: LaterExemplar['content'] | WordsAndSentencesContent['content']
): IHighlighted => {
  return Object.entries(content).reduce((highlights: IHighlighted, [, side]) => {
    side.forEach((section: Section) => {
      if ('lines' in section) {
        section.lines.forEach((line: ContentLine) => {
          line.words.forEach(word => {
            if ('highlight' in word) {
              if (!(word.sectionId in highlights)) {
                highlights[word.sectionId] = {};
              }

              return (highlights[word.sectionId][word.id] = {
                color: word.highlight || '',
                highlighted: true,
              });
            }

            return highlights;
          });
        });
      }

      return highlights;
    });

    return highlights;
  }, {});
};

export const getHiddenImagesFromSavedContent = (
  content: LaterExemplar['content'] | WordsAndSentencesContent['content']
): string[] => {
  return Object.entries(content).reduce((hiddenImages: string[], [, side]) => {
    side.forEach((section: Image) => {
      if (section.type === 'image' && 'isHidden' in section && (section as unknown as IImage).isHidden) {
        const { default_uuid, uuid } = section;
        hiddenImages = [...hiddenImages, getHiddenImageId(default_uuid, uuid)];
      }
    });

    return hiddenImages;
  }, []);
};

export const computeWordsScreenTitle = (title: WordsPageTitle): EditedWordsPageTitle => ({
  ...(title.color && { color: title.color }),
  lines: title.lines.map(line => {
    return {
      words: line.words.map(word => ({
        text: word.text,
        ...(word.style?.length && { style: word.style }),
        ...(word.color && { color: word.color }),
      })),
    };
  }),
});

export const computeSavedPenLines = (lines: LineType[]): SavedPenLine[] => {
  return lines.map(line => ({
    color: line.drawingColor,
    points: line.points,
  }));
};

export const convertSavedLinesToLines = (savedLines: SavedPenLine[]): LineType[] => {
  return savedLines.map(line => ({
    tool: 'pen',
    drawingColor: line.color,
    points: line.points,
  }));
};

export const computeSavedNotes = (notes: INote[]): SavedNote[] => {
  return notes.map(note => ({
    content: note.content,
    position: note.position,
    isHidden: note.isManuallyHidden,
  }));
};

export const computeSavedWordsImages = (wordsImages: IImage[] = [], images: IImage[] = []): IImage[] => {
  return images.map(image => {
    const savedImage = wordsImages.find(({ uuid, default_uuid }) => [uuid, default_uuid].includes(image.uuid));

    return {
      ...image,
      isHidden: savedImage?.isHidden || false,
      uuid: savedImage?.uuid || image.uuid,
      ...(savedImage?.default_uuid && { default_uuid: savedImage.default_uuid }),
    };
  });
};

export const computeSavedLabels = (labels: ILabel[], words: ILabel[] = []): SavedLabel[] => {
  const restLabels = words.filter(({ position }) => !position).map(({ text }) => ({ text }));
  const savedLabels = labels.map(label => ({
    text: label.text,
    position: label.position,
    isHidden: label.isHidden,
    isManuallyHidden: label.isManuallyHidden,
  }));

  return [...savedLabels, ...restLabels];
};

export const convertSavedNotesToNotes = (notes: SavedNote[]): INote[] => {
  return notes.map((note: SavedNote) => ({
    ...note,
    isManuallyHidden: note.isHidden,
    selectionStart: note.content.length,
    isFocussed: false,
    id: uuid(),
  }));
};

export type ExemplarContextCombined = typeof EmergentContext & typeof LaterContext;

export const getExemplarContext = (stage: string): typeof LaterContext | typeof EmergentContext => {
  return (
    {
      [EMERGENT]: EmergentContext,
      [EARLY]: LaterContext,
      [DEVELOPING]: LaterContext,
      [CONSOLIDATING]: LaterContext,
      [EXTENDING]: LaterContext,
    }[stage] || LaterContext
  );
};
